"use client";

import { useEffect, useState } from "react";
import type { RfqAttachment } from "@/lib/plugins/rfq/types";

type RfqFormState = {
  company: string;
  contact: string;
  email: string;
  phone: string;
  productType: string;
  standard: string;
  specification: string;
  material: string;
  finish: string;
  quantity: string;
  leadTime: string;
  message: string;
  attachment?: RfqAttachment;
};

const initialState: RfqFormState = {
  company: "",
  contact: "",
  email: "",
  phone: "",
  productType: "",
  standard: "",
  specification: "",
  material: "",
  finish: "",
  quantity: "",
  leadTime: "",
  message: "",
};

const RFQ_PREFILL_KEY = "rt-cart-rfq-prefill";

export function RfqForm({
  lang = "zh",
  title,
  intro,
  submitLabel,
}: {
  lang?: "zh" | "en";
  title?: string;
  intro?: string;
  submitLabel?: string;
}) {
  const [form, setForm] = useState<RfqFormState>(initialState);
  const [message, setMessage] = useState(intro || (lang === "en" ? "Submit your RFQ and drawings." : "填写需求并提交询价。"));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(RFQ_PREFILL_KEY);
      if (!raw) return;
      const payload = JSON.parse(raw) as {
        summary?: string;
        totalQuantity?: number;
        customer?: {
          company?: string;
          contact?: string;
          email?: string;
          phone?: string;
        };
      };
      if (!payload.summary) return;
      setForm((current) => ({
        ...current,
        company: current.company || payload.customer?.company || "",
        contact: current.contact || payload.customer?.contact || "",
        email: current.email || payload.customer?.email || "",
        phone: current.phone || payload.customer?.phone || "",
        quantity: current.quantity || (payload.totalQuantity ? String(payload.totalQuantity) : current.quantity),
        message: current.message.includes(payload.summary)
          ? current.message
          : current.message
            ? `${current.message}\n\n${payload.summary}`
            : payload.summary,
      }));
      setMessage(lang === "en" ? "RFQ cart products have been added to the notes." : "询价车产品已带入补充说明。");
    } catch {
      // Ignore invalid cart data.
    }
  }, [lang]);

  function update(patch: Partial<RfqFormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  async function selectAttachment(file: File | null) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setMessage(lang === "en" ? "Attachment must be 5MB or smaller." : "附件不能超过 5MB。");
      return;
    }
    if (!/^(application\/pdf|image\/png|image\/jpeg|image\/jpg|image\/webp)$/i.test(file.type)) {
      setMessage(lang === "en" ? "Only PDF or image attachments are supported." : "仅支持 PDF 或图片附件。");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    update({ attachment: { name: file.name, type: file.type, size: file.size, dataUrl } });
    setMessage(lang === "en" ? "Attachment added." : "附件已添加。");
  }

  async function submit() {
    if (!form.company || !form.contact || (!form.email && !form.phone)) {
      setMessage(lang === "en" ? "Company, contact, and email or phone are required." : "请填写公司、联系人，以及邮箱或电话。");
      return;
    }

    setSubmitting(true);
    setMessage(lang === "en" ? "Submitting..." : "提交中...");
    try {
      const response = await fetch("/api/rfq", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        setMessage(lang === "en" ? "Submit failed. Please check the form." : "提交失败，请检查表单。");
        return;
      }
      setForm(initialState);
      setMessage(lang === "en" ? "RFQ submitted. We will contact you soon." : "询价已提交，我们会尽快联系您。");
    } catch {
      setMessage(lang === "en" ? "Submit failed." : "提交失败。");
    } finally {
      setSubmitting(false);
    }
  }

  const t = labels[lang];

  return (
    <form className="rfq-plugin-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <div className="rfq-plugin-head">
        <span>{t.eyebrow}</span>
        <h2>{title || t.title}</h2>
        <p>{message}</p>
        <small>{t.requiredHint}</small>
      </div>
      <div className="rfq-plugin-grid">
        <Field label={t.company} value={form.company} onChange={(value) => update({ company: value })} required />
        <Field label={t.contact} value={form.contact} onChange={(value) => update({ contact: value })} required />
        <Field label={t.email} value={form.email} onChange={(value) => update({ email: value })} required />
        <Field label={t.phone} value={form.phone} onChange={(value) => update({ phone: value })} required />
        <Field label={t.productType} value={form.productType} onChange={(value) => update({ productType: value })} />
        <Field label={t.standard} value={form.standard} onChange={(value) => update({ standard: value })} />
        <Field label={t.specification} value={form.specification} onChange={(value) => update({ specification: value })} />
        <Field label={t.material} value={form.material} onChange={(value) => update({ material: value })} />
        <Field label={t.finish} value={form.finish} onChange={(value) => update({ finish: value })} />
        <Field label={t.quantity} value={form.quantity} onChange={(value) => update({ quantity: value })} />
        <Field label={t.leadTime} value={form.leadTime} onChange={(value) => update({ leadTime: value })} />
        <label className="rfq-plugin-file">
          {t.attachment}
          <input type="file" accept="application/pdf,image/*" onChange={(event) => void selectAttachment(event.target.files?.[0] ?? null)} />
          <span>{form.attachment?.name || t.attachmentHint}</span>
        </label>
        <label className="rfq-plugin-message">
          {t.message}
          <textarea value={form.message} onChange={(event) => update({ message: event.target.value })} />
        </label>
      </div>
      <button type="submit" disabled={submitting}>{submitting ? t.submitting : submitLabel || t.submit}</button>
    </form>
  );
}

function Field({ label, value, onChange, required = false }: { label: string; value: string; onChange: (value: string) => void; required?: boolean }) {
  return (
    <label>
      <span>{label}{required && <b aria-label="required">*</b>}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const labels = {
  zh: {
    eyebrow: "RFQ",
    title: "图纸询价",
    company: "公司名称",
    contact: "联系人",
    email: "邮箱",
    phone: "电话",
    productType: "产品类型",
    standard: "标准",
    specification: "规格 / 型号",
    material: "材料",
    finish: "表面处理",
    quantity: "数量",
    leadTime: "期望交期",
    attachment: "图纸 / 图片附件",
    attachmentHint: "支持 PDF 或图片，最大 5MB",
    message: "补充说明",
    requiredHint: "* 为必填项；邮箱和电话至少填写一个。",
    submit: "提交询价",
    submitting: "提交中...",
  },
  en: {
    eyebrow: "RFQ",
    title: "Drawing RFQ",
    company: "Company",
    contact: "Contact",
    email: "Email",
    phone: "Phone",
    productType: "Product Type",
    standard: "Standard",
    specification: "Specification / Model",
    material: "Material",
    finish: "Finish",
    quantity: "Quantity",
    leadTime: "Expected Lead Time",
    attachment: "Drawing / Image",
    attachmentHint: "PDF or image, max 5MB",
    message: "Notes",
    requiredHint: "* Required. Email or phone: at least one is required.",
    submit: "Submit RFQ",
    submitting: "Submitting...",
  },
};
